import React from 'react';
import get from 'lodash/get';
import find from 'lodash/find';
import filter from 'lodash/filter';
import { useRecoilState, useRecoilValue } from 'recoil';
import { DataTable } from 'app/components/Charts/table';
import { MUIDataTableIsRowCheck } from 'mui-datatables';
import {
  selectedFilterAtom,
  transactionTypeAtom,
} from 'app/state/recoil/atoms';
import { useStoreActions, useStoreState } from 'app/state/store/hooks';
import {
  SectorsDataTableColumns,
  SectorsDataTableOptions,
} from 'app/components/Charts/table/data';
import {
  getAPIFormattedFilters,
  AF_SECTOR,
} from 'app/utils/getAPIFormattedFilters';
import { ExpandableRows } from 'app/components/Charts/table/common/rows/ExpandableRows';
import { downloadActivitiesCSV } from 'app/utils/downloadActivitiesCSV';

interface SectorsFragmentTableProps {
  pageParam: string;
  detailBaseFilter: string;
  selected: string;
  setSelected: (value: string) => void;
  prevSelections: string[];
  setPrevSelections: (value: string[]) => void;
}

export function SectorsFragmentTable(props: SectorsFragmentTableProps) {
  const [selectedFilters, _] = useRecoilState(selectedFilterAtom);
  const action = useStoreActions((actions) => actions.sectorsTable.fetch);
  const actionData = useStoreState((actions) => actions.sectorsTable.data);
  const transactionType = useRecoilValue(transactionTypeAtom);

  const tableData = get(actionData, 'data', []);
  const tableCount = get(actionData, 'count', []);

  function callAction() {
    const filters = getAPIFormattedFilters(selectedFilters);
    action({
      values: {
        filters: {
          ...filters,
          [props.detailBaseFilter]: [props.pageParam],
        },
        transactionTypeCode: transactionType.code,
      },
    });
  }

  const [shownData, setShownData] = React.useState([]);

  function onSearchChange(value: string | null) {
    if (value) {
      const fvalue = value.toLowerCase();
      setShownData(
        filter(tableData, (td: any) => {
          const sectorCat =
            td.code.toLowerCase().indexOf(fvalue) > -1 ||
            td.title.toLowerCase().indexOf(fvalue) > -1;
          if (sectorCat) {
            return true;
          }
          if (td.children) {
            const dac3 = find(td.children, (child: any) => {
              const dac3child =
                child.code.toLowerCase().indexOf(fvalue) > -1 ||
                child.title.toLowerCase().indexOf(fvalue) > -1;
              if (dac3child) {
                return true;
              }
              if (child.children) {
                return find(
                  child.children,
                  (gchild: any) =>
                    gchild.code.toLowerCase().indexOf(fvalue) > -1 ||
                    gchild.title.toLowerCase().indexOf(fvalue) > -1
                );
              }
              return false;
            });
            if (dac3) {
              return true;
            }
          }
          return false;
        }) as never[]
      );
    } else {
      setShownData(tableData);
    }
  }

  React.useEffect(() => {
    callAction();
  }, [props.pageParam, selectedFilters, transactionType]);

  React.useEffect(() => setShownData(tableData), [tableData]);

  const rowsExpanded: number[] = [];

  if (props.selected.length > 0) {
    tableData.forEach((row: any, index: number) => {
      if (
        row.title === props.selected ||
        (row.children && find(row.children, { title: props.selected }))
      ) {
        rowsExpanded.push(index);
      }
    });
  }

  return (
    <DataTable
      data={shownData}
      columns={SectorsDataTableColumns}
      options={{
        ...SectorsDataTableOptions,
        count: shownData.length,
        onDownload: () => {
          let filters = getAPIFormattedFilters(selectedFilters);
          if (!find(Object.keys(filters), (key: string) => key === AF_SECTOR)) {
            filters = {
              ...filters,
              [AF_SECTOR]: ['*'],
            };
          }
          downloadActivitiesCSV(
            {
              ...filters,
              [props.detailBaseFilter]: [props.pageParam],
            },
            '',
            '',
            20000,
            props.pageParam
          );
          return false;
        },
        onSearchChange,
        // sortOrder: {
        //   name: 'size',
        //   direction: 'desc',
        // },
        // customSort: (data: any[], colIndex: number, order: string) => {
        //   if (colIndex === 0) {
        //     return orderBy(
        //       data,
        //       (item: any) => parseInt(item.data[colIndex], 10),
        //       order === 'asc' ? 'asc' : 'desc'
        //     );
        //   }
        //   return orderBy(
        //     data,
        //     (item: any) => item.data[colIndex],
        //     order === 'asc' ? 'asc' : 'desc'
        //   );
        // },
        isRowExpandable: (
          dataIndex: number,
          expandedRows?: MUIDataTableIsRowCheck
        ) => {
          // @ts-ignore
          return shownData[dataIndex].children;
        },
        renderExpandableRow: (
          rowData: string[],
          rowMeta: { dataIndex: number; rowIndex: number }
        ) => {
          // @ts-ignore
          const childData = shownData[rowMeta.rowIndex].children;
          if (childData) {
            return (
              <ExpandableRows
                level={1}
                data={childData}
                key={rowMeta.dataIndex}
                selected={props.selected}
                setSelected={props.setSelected}
                prevSelections={props.prevSelections}
                setPrevSelections={props.setPrevSelections}
              />
            );
          }
        },
        rowsExpanded,
        onRowExpansionChange: (
          currentRowsExpanded: any[],
          allRowsExpanded: any[]
        ) => {
          if (currentRowsExpanded[0] && allRowsExpanded.length > 0) {
            const item = get(
              tableData[currentRowsExpanded[0].index],
              'title',
              ''
            );
            props.setSelected(item);
            if (item !== '') {
              props.setPrevSelections([item]);
            }
          } else if (props.selected !== '' && allRowsExpanded.length === 0) {
            props.setSelected('');
            props.setPrevSelections([]);
          }
        },
      }}
      title={`AIDA has found ${new Intl.NumberFormat('nl-NL').format(
        tableCount
      )} sectors for you`}
    />
  );
}
